import { FC, useCallback, useMemo } from 'react';
import { isMobileOnly } from 'react-device-detect';
import { Trans, useTranslation } from 'react-i18next';
import {
    VictoryBar,
    VictoryAxis,
    VictoryLine,
    VictoryChart,
    VictoryTooltip,
    VictoryVoronoiContainer,
} from 'victory';

import { Icon } from '@eon-home/react-library';

import { getLocale } from '@store/actions';
import { EXTERNAL_LINK_PROPS } from '@tools/constants';
import { Routes } from '@tools/enums';
import { SpotPriceType } from '@tools/types';
import {
    Moment,
    getRegion,
    LocalizationConfig,
    toLocalizedCurrency,
    shouldHideSpotPriceValues,
} from '@tools/utils';
import { SpotPricePoint } from '.';
import { SideStrokedBar } from './side-stroked-bar';
import { useAppDarkMode, useSiteTimezone } from '@store/selectors';
import {
    shouldHideFlyout,
    getIntervalPeriod,
    getSpotPriceColors,
} from './settings';

import InfoIcon from '@assets/icons/emobility/info.svg';
import Wallbox from '@assets/icons/emobility/wallbox.svg';

interface Props {
    data: SpotPricePoint[];
    type?: SpotPriceType;
}

interface ChartLine {
    data: SpotPricePoint[];
    color: string;
}

const { dateTimePatterns } = LocalizationConfig;

export const SpotPriceGraph: FC<Readonly<Props>> = ({ data, type }: Props) => {
    const { t } = useTranslation();
    const colors = getSpotPriceColors();
    const locale = getLocale();
    const region = getRegion();
    const timezone = useSiteTimezone();
    const { isDarkMode } = useAppDarkMode();
    const shouldHideTooltip = shouldHideSpotPriceValues();

    const formatTime = useCallback(
        (
            timestamp: string | undefined,
            format = dateTimePatterns[locale].onlyTime,
        ): string => Moment(timestamp).tz(timezone).format(format),
        [locale, timezone],
    );

    const formatDate = useCallback(
        // prettier-ignore
        (value: string, adjustment = 0) => Moment(value).add(adjustment, 'minutes').tz(timezone).format('H:mm'),
        [timezone],
    );

    const schedule = useMemo(
        () =>
            Array.from(new Set(data.map((item) => item.z)))
                .filter(Boolean)
                .map((item) => ({
                    end: formatTime(item?.end),
                    start: formatTime(item?.start),
                    date: formatTime(
                        item?.start,
                        dateTimePatterns[locale].graphMonth,
                    ),
                })),
        [data, locale, formatTime],
    );

    const max = useMemo(
        () =>
            data.length
                ? // prettier-ignore
                  Math.ceil(Math.max(...data.map((value) => value.y ?? 0)) * 10) / 10
                : 0,
        [data],
    );

    const min = useMemo(() => {
        const minValue = data.length
            ? // prettier-ignore
              Math.floor(Math.min(...data.map((value) => value.y ?? 0)) * 10) / 10
            : 0;

        // Min should be 0 if only positive values are shown
        return minValue > 0 ? 0 : minValue;
    }, [data]);

    const hasChartNegativeValues = useMemo(() => min < 0, [min]);

    const lineSections = useMemo((): ChartLine[] => {
        const switchDirectionIndexes = data?.reduce(
            (result: number[], currItem: SpotPricePoint, index: number) => {
                if (index === 0) {
                    return [...result, index];
                }

                const isPrevItemNegative =
                    // @ts-ignore
                    data[index - 1].y != null && data[index - 1].y < 0;
                const isCurrItemNegative = currItem.y != null && currItem.y < 0;

                const hasSameDirection =
                    isPrevItemNegative === isCurrItemNegative;

                if (!hasSameDirection) return [...result, index];

                return result;
            },
            [],
        );

        const barSections = switchDirectionIndexes.reduce(
            (sections: ChartLine[], currItem: number, index: number) => {
                const section: ChartLine = {
                    data: [],
                    color: colors.turquoise,
                };

                const sliceStart = currItem;
                const sliceEnd =
                    index === switchDirectionIndexes.length - 1
                        ? undefined
                        : switchDirectionIndexes[index + 1];

                let sectiondata = data.slice(sliceStart, sliceEnd);

                if (!!sliceEnd && hasChartNegativeValues)
                    sectiondata.push({
                        ...data[sliceEnd],
                        isAdditional: true,
                        y: 0,
                    });

                const isNegativeValSection =
                    sectiondata[0].y != null && sectiondata[0].y < 0;

                if (index !== 0 && hasChartNegativeValues)
                    sectiondata = [
                        {
                            ...data[sliceStart],
                            y: 0,
                            isAdditional: true,
                        },
                        ...sectiondata,
                    ];

                return [
                    ...sections,
                    {
                        ...section,
                        data: sectiondata,
                        color: isNegativeValSection
                            ? colors.limeyellow
                            : colors.turquoise,
                    },
                ];
            },
            [],
        );

        return barSections;
    }, [colors.limeyellow, colors.turquoise, data, hasChartNegativeValues]);

    return (
        <>
            <div className="c-spot-price__scroller">
                <VictoryChart
                    width={890}
                    height={hasChartNegativeValues ? 250 : 300}
                    padding={{
                        top: 15,
                        right: isMobileOnly ? 80 : 5,
                        bottom: 20,
                        left: shouldHideTooltip ? 5 : 40,
                    }}
                    standalone
                    containerComponent={
                        <VictoryVoronoiContainer responsive={false} />
                    }
                >
                    {/** Zero X axis */}
                    <VictoryAxis
                        style={{
                            axis: {
                                stroke: colors.lightgrey,
                                strokeOpacity: isDarkMode ? 0.2 : 0.8,
                            },
                            grid: {
                                stroke: 'none',
                            },
                            tickLabels: {
                                fill: colors.grey,
                                padding: 4,
                                // Hide labels on Zero axis if we have NEG values
                                fontSize: hasChartNegativeValues ? 0 : 9,
                                fontFamily: 'EON',
                            },
                        }}
                        tickFormat={(value: string) => formatDate(value)}
                        orientation="bottom"
                        dependentAxis={false}
                        fixLabelOverlap
                    />

                    {/** Min X axis in case we have negative values
                     *   and need to render the X axis on bottom where min Y value is */}
                    {hasChartNegativeValues && (
                        <VictoryAxis
                            style={{
                                axis: {
                                    stroke: colors.lightgrey,
                                    strokeOpacity: isDarkMode ? 0.2 : 0.8,
                                },
                                grid: {
                                    stroke: 'none',
                                },
                                tickLabels: {
                                    fill: colors.grey,
                                    padding: 4,
                                    fontSize: 9,
                                    fontFamily: 'EON',
                                },
                            }}
                            tickFormat={(value: string) => formatDate(value)}
                            orientation="bottom"
                            dependentAxis={false}
                            fixLabelOverlap
                            axisValue={min}
                        />
                    )}

                    {!shouldHideTooltip && (
                        <VictoryAxis
                            style={{
                                axis: {
                                    stroke: 'none',
                                },
                                grid: {
                                    stroke: colors.lightgrey,
                                    strokeOpacity: isDarkMode ? 0.2 : 0.8,
                                    strokeDasharray: 4,
                                },
                                axisLabel: {
                                    fill: colors.grey,
                                    fontSize: 12,
                                    fontFamily: 'EON',
                                },
                                tickLabels: {
                                    fill: colors.grey,
                                    filter: 'url(#axis-label-background)',
                                    padding: 0,
                                    fontSize: 10,
                                    fontFamily: 'EON',
                                },
                            }}
                            label={`${LocalizationConfig.currencyUnitShorthand[region]}/kWh`}
                            offsetX={52}
                            orientation="left"
                            dependentAxis
                            fixLabelOverlap
                        />
                    )}

                    {!!lineSections.length &&
                        lineSections.map((line, index) => (
                            <VictoryLine
                                key={index}
                                data={line.data}
                                style={{
                                    data: {
                                        stroke: line.color,
                                    },
                                    parent: {
                                        border: colors.grey,
                                    },
                                }}
                                domain={{
                                    x: [0, data.length],
                                    y: [min, max],
                                }}
                                labels={({ datum, index }: any) => {
                                    if (shouldHideTooltip) return null;

                                    if (
                                        shouldHideFlyout(index, data.length) ||
                                        datum.isAdditional
                                    ) {
                                        return '';
                                    }

                                    const { x, y } = datum;
                                    const end = formatDate(
                                        x,
                                        getIntervalPeriod(data),
                                    );
                                    const start = formatDate(x);
                                    const value = toLocalizedCurrency(y);

                                    return y === null
                                        ? 'N/A'
                                        : `${start}-${end}: ${value}`;
                                }}
                                labelComponent={
                                    shouldHideTooltip ? undefined : (
                                        <VictoryTooltip
                                            style={{
                                                fill: colors.darkgrey,
                                                padding: 0,
                                                fontSize: 12,
                                                fontFamily: 'EON',
                                            }}
                                            flyoutStyle={{
                                                filter: 'url(#tooltip-shadow)',
                                                strokeWidth: 0,
                                            }}
                                            /**
                                             * All adjustments of the Tooltip style below are needed because
                                             * we don't want to show the tooltip for any data entry which is
                                             * after the 24th hour of the day. The data fed to the graph contains
                                             * 25 data entries due to the need to show a bar for the timeframe
                                             * between 23:00 and 00:00 on the next day. The tooltip for this
                                             * time frame, however, is not needed and is actually misleading.
                                             * That's why we technically hide the tooltip by setting all of its
                                             * visual properties to zero.
                                             */
                                            // prettier-ignore
                                            flyoutWidth={({ index, datum }) => shouldHideFlyout(index, data.length) || datum.isAdditional ? 0 : 140}
                                            // prettier-ignore
                                            flyoutHeight={({ index, datum }) => shouldHideFlyout(index, data.length) || datum.isAdditional ? 0 : 26}
                                            // prettier-ignore
                                            flyoutPadding={({ index, datum }) => shouldHideFlyout(index, data.length) || datum.isAdditional ? 0 : 5}
                                            // prettier-ignore
                                            cornerRadius={({ index, datum }) => shouldHideFlyout(index, data.length) || datum.isAdditional ? 0 : 2}
                                            // prettier-ignore
                                            pointerWidth={({ index, datum }) => shouldHideFlyout(index, data.length) || datum.isAdditional ? 0 : 10}
                                            // prettier-ignore
                                            pointerLength={({ index, datum }) => shouldHideFlyout(index, data.length) || datum.isAdditional ? 0 : 5}
                                        />
                                    )
                                }
                                interpolation="stepAfter"
                            />
                        ))}

                    {type !== 'limited' && (
                        <VictoryBar
                            data={data.map((item, index) => ({
                                x: index,
                                y: item.z ? max : null,
                            }))}
                            style={{
                                data: {
                                    fill: 'url(#diagonal-hatch)',
                                },
                            }}
                            padding={0}
                            barRatio={1.075}
                            alignment="start"
                            cornerRadius={0}
                            dataComponent={<SideStrokedBar width={35.6} />}
                        />
                    )}
                    {/**
                     * Handle bars for charging intervals below Zero axis
                     */}
                    {type !== 'limited' && hasChartNegativeValues && (
                        <VictoryBar
                            data={data.map((item, index) => ({
                                x: index,
                                y: item.z ? min : null,
                            }))}
                            style={{
                                data: {
                                    fill: 'url(#diagonal-hatch)',
                                },
                            }}
                            padding={0}
                            barRatio={1.075}
                            alignment="start"
                            cornerRadius={0}
                            dataComponent={<SideStrokedBar width={35.6} />}
                        />
                    )}
                </VictoryChart>
            </div>

            {hasChartNegativeValues && (
                <div className="c-spot-price__negative-values-info">
                    <Icon
                        src={InfoIcon}
                        className="c-spot-price__negative-values-info-icon"
                    />
                    <p>
                        <Trans
                            defaults={t(
                                '<strong>Negative spot prices</strong> are seen. <a>Find out more on what it means.</a>',
                            )}
                            components={{
                                a: (
                                    <a
                                        {...EXTERNAL_LINK_PROPS}
                                        href={`${Routes.MORE_FAQ}#ev&negative-spot-price`}
                                        className="c-spot-price__negative-values-info-link"
                                    />
                                ),
                                strong: <strong />,
                            }}
                        />
                    </p>
                </div>
            )}

            {schedule.length > 0 && type !== 'limited' ? (
                <div className="c-emobility__infobox">
                    <Icon
                        src={Wallbox}
                        className="c-spot-price__infobox-icon"
                    />

                    {schedule.length > 1 ? (
                        <p>
                            {t(
                                'Your car will be charged based on dynamic price charging between {{ start1 }} and {{ end1 }} and between {{ start2 }} and {{ end2 }} on the {{ date }}.',
                                {
                                    end1: schedule[0].end,
                                    end2: schedule[1].end,
                                    date: schedule[0].date,
                                    start1: schedule[0].start,
                                    start2: schedule[1].start,
                                },
                            )}
                        </p>
                    ) : (
                        <p>
                            {t(
                                'Your car will be charged based on dynamic price charging between {{ start }} and {{ end }} on the {{ date }}.',
                                schedule[0],
                            )}
                        </p>
                    )}
                </div>
            ) : null}
        </>
    );
};

export default SpotPriceGraph;
